// 2026-04-26 (M80 Phase D.2): Admin dashboard học nội quy theo vòng
//
// Layout: 1 trang với 3 view qua state:
//   - 'overview': stats hôm nay + table NV
//   - 'employee': sessions của 1 NV (click row → enter)
//   - 'set':      chi tiết 1 bộ (click 1 session → enter)
//
// Route: /admin/rules-m80

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuthStore } from '../lib/auth.js';
import { hasPermission } from '../lib/permissions.js';
import { api } from '../lib/api.js';

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

// ----------------------------------------------------------------------------
// View: Overview
// ----------------------------------------------------------------------------
function OverviewView({ date, setDate, onPickEmployee, onOpenSettings }) {
  const [stats, setStats] = useState(null);
  const [employees, setEmployees] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [s, e] = await Promise.all([
          api.adminRulesM80TodayStats(date),
          api.adminRulesM80EmployeesSummary(date),
        ]);
        if (cancelled) return;
        setStats(s);
        setEmployees(e?.employees || []);
      } catch (err) {
        if (!cancelled) toast.error('Lỗi load: ' + (err?.data?.error || err?.message));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [date]);

  return (
    <>
      {/* Filter date + nút settings */}
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <label className="text-sm text-slate-600">Ngày:</label>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm"
        />
        <button
          onClick={() => setDate(todayIso())}
          className="px-3 py-1.5 text-sm bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
        >
          Hôm nay
        </button>
        <div className="flex-1" />
        <button
          onClick={onOpenSettings}
          className="px-3 py-1.5 text-sm bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
        >
          ⚙️ Cài đặt M80
        </button>
      </div>

      {loading && <div className="text-sm text-slate-500">Đang tải...</div>}

      {!loading && stats && (
        <>
          {/* Stats cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            <StatCard label="NV đã học" value={stats.employees_active_today} sub={`/${stats.employees_total}`} color="sky" />
            <StatCard label="Đạt mức tối thiểu" value={stats.employees_done_min} sub={`≥${stats.daily_min_chapter} chương`} color="emerald" />
            <StatCard label="Chưa học" value={stats.employees_not_done} color="rose" />
            <StatCard label="Câu trả lời" value={stats.answers_total} sub={`${stats.answers_wrong} sai`} color="indigo" />
            <StatCard label="Lượt xem phao" value={stats.peeks_total} color="amber" />
            <StatCard label="Chi phí AI" value={`${stats.cost_vnd.toLocaleString('vi-VN')}đ`} color="slate" />
          </div>

          {/* Table NV */}
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 border-b bg-slate-50 font-semibold text-slate-700">
              Chi tiết nhân viên ({employees.length})
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Nhân viên</th>
                    <th className="px-3 py-2 text-center">Vòng</th>
                    <th className="px-3 py-2 text-center">Bộ tạo</th>
                    <th className="px-3 py-2 text-center">Bộ xong</th>
                    <th className="px-3 py-2 text-center">Câu trả lời</th>
                    <th className="px-3 py-2 text-center">Sai</th>
                    <th className="px-3 py-2 text-center">Phao</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {employees.length === 0 ? (
                    <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400">Chưa có hoạt động</td></tr>
                  ) : employees.map(emp => (
                    <tr key={emp.id} className="border-t hover:bg-sky-50/50">
                      <td className="px-3 py-2 font-medium text-slate-800">{emp.full_name}</td>
                      <td className="px-3 py-2 text-center">
                        {emp.current_round && (
                          <span className="px-2 py-0.5 bg-sky-100 text-sky-700 rounded text-xs font-semibold">
                            V{emp.current_round}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">{emp.sets_today || 0}</td>
                      <td className="px-3 py-2 text-center text-emerald-600 font-semibold">{emp.sets_done_today || 0}</td>
                      <td className="px-3 py-2 text-center">{emp.answers_today || 0}</td>
                      <td className={`px-3 py-2 text-center font-semibold ${emp.wrong_today > 0 ? 'text-rose-600' : 'text-slate-400'}`}>
                        {emp.wrong_today || 0}
                      </td>
                      <td className={`px-3 py-2 text-center font-semibold ${emp.peeks_today > 0 ? 'text-amber-600' : 'text-slate-400'}`}>
                        {emp.peeks_today || 0}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => onPickEmployee(emp)}
                          className="text-xs px-2 py-1 bg-sky-600 hover:bg-sky-700 text-white rounded"
                        >
                          Xem chi tiết →
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </>
  );
}

function StatCard({ label, value, sub, color = 'slate' }) {
  const colorMap = {
    sky:     'border-sky-200 bg-sky-50 text-sky-700',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    rose:    'border-rose-200 bg-rose-50 text-rose-700',
    indigo:  'border-indigo-200 bg-indigo-50 text-indigo-700',
    amber:   'border-amber-200 bg-amber-50 text-amber-700',
    slate:   'border-slate-200 bg-slate-50 text-slate-700',
  };
  return (
    <div className={`rounded-xl border-2 p-3 ${colorMap[color] || colorMap.slate}`}>
      <div className="text-xs font-semibold uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
      {sub && <div className="text-xs opacity-60 mt-0.5">{sub}</div>}
    </div>
  );
}

// ----------------------------------------------------------------------------
// View: Employee — list sessions
// ----------------------------------------------------------------------------
function EmployeeView({ employee, onBack, onPickSet }) {
  const [sessions, setSessions] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.adminRulesM80EmployeeSessions(employee.id);
        if (!cancelled) setSessions(r?.sessions || []);
      } catch (e) {
        if (!cancelled) toast.error('Lỗi load: ' + (e?.data?.error || e?.message));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [employee.id]);

  return (
    <>
      <div className="flex items-center gap-2 mb-4">
        <button onClick={onBack} className="text-sm px-3 py-1.5 bg-white border border-slate-300 rounded-lg hover:bg-slate-50">
          ← Về danh sách
        </button>
        <h2 className="text-lg font-semibold text-slate-800">📋 {employee.full_name}</h2>
      </div>

      {loading && <div className="text-sm text-slate-500">Đang tải...</div>}

      {!loading && sessions && (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 border-b bg-slate-50 font-semibold text-slate-700">
            Tất cả bộ câu hỏi ({sessions.length})
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Thời gian</th>
                  <th className="px-3 py-2 text-left">Chương</th>
                  <th className="px-3 py-2 text-center">Vòng</th>
                  <th className="px-3 py-2 text-center">Loại</th>
                  <th className="px-3 py-2 text-center">Trạng thái</th>
                  <th className="px-3 py-2 text-center">Câu/Pass</th>
                  <th className="px-3 py-2 text-center">Sai</th>
                  <th className="px-3 py-2 text-center">Phao</th>
                  <th className="px-3 py-2 text-center">VND</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {sessions.length === 0 ? (
                  <tr><td colSpan={10} className="px-3 py-8 text-center text-slate-400">Chưa có bộ nào</td></tr>
                ) : sessions.map(s => (
                  <tr key={s.id} className="border-t hover:bg-sky-50/50">
                    <td className="px-3 py-2 text-xs text-slate-500">{new Date(s.created_at).toLocaleString('vi-VN')}</td>
                    <td className="px-3 py-2 text-slate-800">{s.module_title}</td>
                    <td className="px-3 py-2 text-center">
                      <span className="px-1.5 py-0.5 bg-sky-100 text-sky-700 rounded text-xs">V{s.round_no}</span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      {s.is_review ? (
                        <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">🔄 Ôn</span>
                      ) : (
                        <span className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">📚 Học</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {s.status === 'done' ? (
                        <span className="text-emerald-600 font-semibold">✅ Xong</span>
                      ) : (
                        <span className="text-amber-600">⏳ Đang làm</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">{s.attempts_passed}/{s.attempts_count}</td>
                    <td className={`px-3 py-2 text-center font-semibold ${s.wrong_count > 0 ? 'text-rose-600' : 'text-slate-400'}`}>
                      {s.wrong_count}
                    </td>
                    <td className={`px-3 py-2 text-center font-semibold ${s.peek_count > 0 ? 'text-amber-600' : 'text-slate-400'}`}>
                      {s.peek_count}
                    </td>
                    <td className="px-3 py-2 text-center text-xs text-slate-500">
                      {(s.cost_vnd || 0).toLocaleString('vi-VN')}đ
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => onPickSet(s)} className="text-xs px-2 py-1 bg-sky-600 hover:bg-sky-700 text-white rounded">
                        Chi tiết →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

// ----------------------------------------------------------------------------
// View: Set — chi tiết 1 bộ (attempts + answers + peek log)
// ----------------------------------------------------------------------------
function SetDetailsView({ setId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.adminRulesM80SetDetails(setId);
        if (!cancelled) setData(r);
      } catch (e) {
        if (!cancelled) toast.error('Lỗi load: ' + (e?.data?.error || e?.message));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [setId]);

  if (loading) return <div className="text-sm text-slate-500">Đang tải...</div>;
  if (!data) return <div className="text-sm text-rose-500">Không tìm thấy</div>;

  return (
    <>
      <div className="flex items-center gap-2 mb-4">
        <button onClick={onBack} className="text-sm px-3 py-1.5 bg-white border border-slate-300 rounded-lg hover:bg-slate-50">
          ← Về danh sách bộ
        </button>
        <h2 className="text-lg font-semibold text-slate-800">📄 Bộ #{data.set.id}</h2>
        <span className="text-sm text-slate-500">— {data.set.module_title} (Vòng {data.set.round_no}, NV: {data.set.employee_name})</span>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div><span className="text-slate-500">Tạo:</span> {new Date(data.set.created_at).toLocaleString('vi-VN')}</div>
          <div><span className="text-slate-500">Hoàn thành:</span> {data.set.completed_at ? new Date(data.set.completed_at).toLocaleString('vi-VN') : 'Chưa'}</div>
          <div><span className="text-slate-500">AI:</span> {data.set.ai_provider}/{data.set.ai_model}</div>
          <div><span className="text-slate-500">Chi phí:</span> {(data.set.cost_vnd || 0).toLocaleString('vi-VN')}đ</div>
          <div><span className="text-slate-500">Loại:</span> {data.set.is_review ? '🔄 Vòng ôn' : '📚 Học mới'}</div>
          <div><span className="text-slate-500">Tổng phao:</span> {data.peek_log.length}</div>
        </div>
      </div>

      <div className="space-y-3">
        {data.attempts.map((a, i) => (
          <div key={a.id} className={`bg-white rounded-xl border p-4 ${a.is_correct === 1 ? 'border-emerald-200' : 'border-rose-200'}`}>
            <div className="flex items-start gap-3 mb-2">
              <div className={`w-8 h-8 rounded-full font-bold flex items-center justify-center flex-shrink-0 text-sm ${
                a.is_correct === 1 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
              }`}>
                {a.is_correct === 1 ? '✓' : a.answers.length || '?'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-slate-500 mb-1">
                  Câu #{i + 1}
                  {a.source_attempt_id && <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">🔁 Lặp từ #{a.source_attempt_id}</span>}
                </div>
                <div className="font-medium text-slate-800">{a.question_text}</div>
                <div className="text-sm text-emerald-700 mt-1">
                  <span className="text-slate-500 text-xs">Đáp án mẫu:</span> {a.correct_answer}
                </div>
              </div>
            </div>

            {/* List answers */}
            {a.answers.length > 0 && (
              <div className="mt-3 pl-11 space-y-2">
                {a.answers.map((ans, j) => (
                  <div key={ans.id} className={`text-sm p-2 rounded border ${
                    ans.is_correct ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'
                  }`}>
                    <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                      <span>{ans.is_correct ? '✅' : '❌'}</span>
                      <span>Lần {j + 1} • {new Date(ans.ts).toLocaleString('vi-VN')}</span>
                      {ans.peek_count_at_answer > 0 && (
                        <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">👀 {ans.peek_count_at_answer} phao</span>
                      )}
                    </div>
                    <div className="text-slate-800">{ans.answer_text}</div>
                    {ans.ai_feedback && !ans.is_correct && (
                      <div className="text-xs text-slate-500 mt-1 italic">→ {ans.ai_feedback}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

// ----------------------------------------------------------------------------
// Settings modal
// ----------------------------------------------------------------------------
function SettingsModal({ onClose }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setSettings(await api.adminRulesM80SettingsGet());
      } catch (e) { toast.error('Lỗi load settings'); }
    })();
  }, []);

  async function handleSave() {
    if (saving || !settings) return;
    setSaving(true);
    try {
      await api.adminRulesM80SettingsUpdate({
        questions_per_set: settings.questions_per_set,
        recommend_threshold: settings.recommend_threshold,
        daily_min_chapter: settings.daily_min_chapter,
      });
      toast.success('✅ Đã lưu');
      onClose();
    } catch (e) {
      toast.error('Lỗi lưu: ' + (e?.data?.error || e?.message));
    } finally { setSaving(false); }
  }

  if (!settings) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-slate-800 mb-4">⚙️ Cài đặt M80</h3>
        <div className="space-y-4">
          <Field
            label="Số câu hỏi / lần kiểm tra"
            value={settings.questions_per_set}
            min={1} max={15}
            onChange={(v) => setSettings(s => ({ ...s, questions_per_set: v }))}
            hint="AI sẽ sinh đúng số câu này mỗi lần NV bấm 'Kiểm tra'. Nếu bộ trước có sai → thêm câu lặp ngoài số này."
          />
          <Field
            label="Ngưỡng câu khó (sai × 2 + phao × 1)"
            value={settings.recommend_threshold}
            min={1} max={20}
            onChange={(v) => setSettings(s => ({ ...s, recommend_threshold: v }))}
            hint="Câu nào điểm khó ≥ ngưỡng → AI khuyến nghị ôn lại sang vòng kế."
          />
          <Field
            label="Số chương tối thiểu / ngày"
            value={settings.daily_min_chapter}
            min={0} max={10}
            onChange={(v) => setSettings(s => ({ ...s, daily_min_chapter: v }))}
            hint="NV phải hoàn thành ít nhất N bộ/ngày. Telegram alert nếu thiếu (Phase E)."
          />
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm bg-white border border-slate-300 rounded-lg hover:bg-slate-50">Huỷ</button>
          <button onClick={handleSave} disabled={saving} className="px-5 py-2 text-sm bg-sky-600 hover:bg-sky-700 text-white rounded-lg disabled:opacity-50 font-semibold">
            {saving ? 'Đang lưu...' : 'Lưu'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, min, max, onChange, hint }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
      <input
        type="number"
        value={value}
        min={min} max={max}
        onChange={(e) => onChange(parseInt(e.target.value, 10) || min)}
        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:border-sky-500 focus:ring-1 focus:ring-sky-500 outline-none"
      />
      {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Main page
// ----------------------------------------------------------------------------
export default function AdminRulesM80Page() {
  const user = useAuthStore(s => s.user);
  const [date, setDate] = useState(todayIso());
  const [view, setView] = useState('overview');     // 'overview' | 'employee' | 'set'
  const [pickedEmployee, setPickedEmployee] = useState(null);
  const [pickedSetId, setPickedSetId] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  if (!hasPermission(user, 'rules_m80')) {
    return <div className="p-6 text-rose-600">403 — bạn không có quyền xem trang này.</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-2 md:gap-3 flex-wrap">
          <Link to="/admin" className="text-sm text-slate-500 hover:text-slate-700 shrink-0">← Trang admin</Link>
          <h1 className="text-base md:text-lg font-semibold">📚 Học nội quy M80</h1>
          <span className="text-xs text-slate-400 hidden md:inline">Theo dõi NV học theo vòng + AI on-demand</span>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 md:p-6">
        {view === 'overview' && (
          <OverviewView
            date={date}
            setDate={setDate}
            onPickEmployee={(emp) => { setPickedEmployee(emp); setView('employee'); }}
            onOpenSettings={() => setShowSettings(true)}
          />
        )}
        {view === 'employee' && pickedEmployee && (
          <EmployeeView
            employee={pickedEmployee}
            onBack={() => { setView('overview'); setPickedEmployee(null); }}
            onPickSet={(s) => { setPickedSetId(s.id); setView('set'); }}
          />
        )}
        {view === 'set' && pickedSetId && (
          <SetDetailsView
            setId={pickedSetId}
            onBack={() => { setView('employee'); setPickedSetId(null); }}
          />
        )}
      </main>

      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
    </div>
  );
}
